import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/about`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;


const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About SEVAA Foundation",
  alternateName: "About Swadesh Sevaa Charitable Foundation",
  url: pageUrl,
  inLanguage: ["en-IN", "hi-IN"],
  description:
    "Learn about SEVAA Foundation, a people-centric nonprofit organization working for tribal communities and families living in extreme poverty through education, healthcare and food security programs.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: logoImage,
    areaServed: {
      "@type": "Country",
      name: "India",
    },
  },
  about: [
    "Tribal Community Support",
    "Rural Development",
    "Child Education",
    "Healthcare Support",
    "Food Security",
    "Hunger Relief",
    "Extreme Poverty Support",
    "Grassroots Social Work",
    "Jharkhand NGO Work",
    "Charity Donation",
    "Volunteer Work",
  ],
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "NGO",
  "@id": `${siteUrl}/#ngo`,
  name: "SEVAA Foundation",
  alternateName: "Swadesh Sevaa Charitable Foundation",
  url: siteUrl,
  logo: logoImage,
  image: defaultImage,
  slogan: "सेवा परमो धर्मः",
  description:
    "SEVAA Foundation is a people-centric nonprofit organization working to support tribal communities and vulnerable families through education, healthcare and food security programs.",
  areaServed: [
    {
      "@type": "Place",
      name: "India",
    },
    {
      "@type": "Place",
      name: "Jharkhand",
    },
    {
      "@type": "Place",
      name: "Chatra",
    },
    {
      "@type": "Place",
      name: "Pakur",
    },
  ],
  knowsAbout: [
    "Tribal Welfare",
    "Child Education",
    "Healthcare Support",
    "Food Security",
    "Hunger Relief",
    "Rural Development",
    "Extreme Poverty Support",
    "Community Development",
    "Volunteer Programs",
    "Charity Donation",
  ],
  sameAs: [siteUrl],
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "About",
      item: pageUrl,
    },
  ],
};

// 1. HERO / INTRODUCTION SECTION
const HeroSection = () => {
  return (
    <section aria-labelledby="about-heading" className="relative w-full">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://content.ckcjewellers.com/media/ckc/images/staticsImage/charityBanner.jpg"
          alt="About SEVAA Foundation supporting tribal communities through education healthcare and food security"
          className="w-full h-full object-cover"
          loading="eager"
        />

        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 text-center text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-8 h-[2px] bg-[#ffb000]"></span>

            <span className="text-[#ffb000] font-black text-xs uppercase tracking-widest">
              About Swadesh Sevaa
            </span>

            <span className="w-8 h-[2px] bg-[#ffb000]"></span>
          </div>

          <h1
            id="about-heading"
            className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6"
          >
            “सेवा परमो धर्मः” <br className="hidden md:block" />
            Empowering{" "}
            <span className="text-orange-500">Tribal Communities</span>
          </h1>

          <p className="text-white/80 text-base md:text-lg font-medium max-w-2xl mx-auto leading-relaxed">
            SEVAA Foundation is a people-centric nonprofit organization working
            to support families living in extreme poverty by addressing three
            critical needs: education, healthcare and food security.
          </p>

          <p className="sr-only">
            SEVAA Foundation, Swadesh Sevaa Charitable Foundation, NGO in India,
            tribal community NGO, Jharkhand NGO, Chatra NGO, Pakur NGO, rural
            development NGO, education NGO, healthcare NGO, food security NGO,
            समाज सेवा, सेवा परमो धर्मः, आदिवासी समुदाय सहायता, गरीब बच्चों की
            मदद, बच्चों की पढ़ाई, स्वास्थ्य सेवा, भोजन सहायता.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// 2. OUR STORY SECTION
const OurStorySection = () => {
  const scrollToDetails = () => {
    const section = document.getElementById("about-details");

    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      aria-labelledby="our-story-heading"
      className="max-w-7xl mx-auto px-6 lg:px-8 py-20"
    >
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="text-slate-400 font-bold text-xs uppercase tracking-widest">
              Know About Us
            </span>
            <span className="w-12 h-[2px] bg-slate-200"></span>
          </div>

          <h2
            id="our-story-heading"
            className="text-3xl md:text-4xl font-black text-slate-900 leading-[1.2] tracking-tight mb-6"
          >
            Grassroots Action for <br />
            Rural & Tribal India
          </h2>

          <p className="text-slate-500 font-medium leading-relaxed mb-6 text-sm md:text-base">
            While SEVAA is newly registered as a charitable foundation, our
            commitment to service has existed long before formal recognition.
            Our team and volunteers have been actively helping vulnerable
            families through informal efforts — supporting children&apos;s
            education, providing food to those in need and assisting with basic
            medical care.
          </p>

          <p className="text-slate-500 font-medium leading-relaxed mb-8 text-sm md:text-base">
            Based in Noida, our formal operations began in some of the most
            economically challenged districts of Jharkhand, including Chatra and
            Pakur. Formal registration now allows us to expand this work{" "}
            <strong className="text-slate-800">
              responsibly, transparently and sustainably.
            </strong>
          </p>

          <button
            onClick={scrollToDetails}
            className="bg-[#0f766e] text-white font-bold text-sm uppercase tracking-widest px-8 py-4 rounded-xl shadow-lg shadow-teal-900/20 hover:bg-slate-900 hover:-translate-y-1 transition-all duration-300"
            aria-label="Read full story about SEVAA Foundation"
          >
            Read Full Story
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[400px] md:h-[500px]">
            <img
              src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070"
              alt="SEVAA Foundation NGO work for tribal children education and rural community welfare"
              className="w-full h-full object-cover"
              loading="lazy"
            />

            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <div
                className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-2xl cursor-pointer hover:scale-110 transition-transform"
                aria-hidden="true"
              >
                <svg
                  className="w-8 h-8 text-[#0f766e] ml-1"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 hidden md:block">
            <p className="text-3xl font-black text-[#ffb000]">
              3<span className="text-[#0f766e]"> Pillars</span>
            </p>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">
              Edu, Health, Food
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// 3. TRANSPARENCY & IMPACT SECTION
const TransparencySection = () => {
  return (
    <section
      aria-labelledby="transparency-heading"
      className="relative bg-[#f8fafc] py-20 border-y border-slate-100 overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2' cy='2' r='1' fill='%230f766e'/%3E%3C/svg%3E")`,
        }}
      ></div>

      <div
        className="absolute -top-40 -left-40 w-[500px] h-[500px] text-[#0f766e] opacity-5 pointer-events-none animate-[spin_60s_linear_infinite]"
        aria-hidden="true"
      >
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
          <g transform="translate(100, 100)">
            <path d="M0 -100 C 40 -40 40 -40 100 0 C 40 40 40 40 0 100 C -40 40 -40 40 -100 0 C -40 -40 -40 -40 0 -100 Z" opacity="0.6" />
            <path d="M0 -100 C 40 -40 40 -40 100 0 C 40 40 40 40 0 100 C -40 40 -40 40 -100 0 C -40 -40 -40 -40 0 -100 Z" transform="rotate(45)" opacity="0.4" />
            <path d="M0 -70 C 20 -20 20 -20 70 0 C 20 20 20 20 0 70 C -20 20 -20 -20 -70 0 C -20 -20 -20 -20 0 -70 Z" transform="rotate(22.5)" opacity="0.8" />
            <path d="M0 -70 C 20 -20 20 -20 70 0 C 20 20 20 20 0 70 C -20 20 -20 -20 -70 0 C -20 -20 -20 -20 0 -70 Z" transform="rotate(67.5)" opacity="0.8" />
            <circle cx="0" cy="0" r="12" opacity="1" />
          </g>
        </svg>
      </div>

      <div
        className="absolute -bottom-40 -right-40 w-[500px] h-[500px] text-[#ffb000] opacity-10 pointer-events-none animate-[spin_60s_linear_infinite_reverse]"
        aria-hidden="true"
      >
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
          <g transform="translate(100, 100)">
            <path d="M0 -100 C 40 -40 40 -40 100 0 C 40 40 40 40 0 100 C -40 40 -40 40 -100 0 C -40 -40 -40 -40 0 -100 Z" opacity="0.6" />
            <path d="M0 -100 C 40 -40 40 -40 100 0 C 40 40 40 40 0 100 C -40 40 -40 40 -100 0 C -40 -40 -40 -40 0 -100 Z" transform="rotate(45)" opacity="0.4" />
            <path d="M0 -70 C 20 -20 20 -20 70 0 C 20 20 20 20 0 70 C -20 20 -20 -20 -70 0 C -20 -20 -20 -20 0 -70 Z" transform="rotate(22.5)" opacity="0.8" />
            <path d="M0 -70 C 20 -20 20 -20 70 0 C 20 20 20 20 0 70 C -20 20 -20 -20 -70 0 C -20 -20 -20 -20 0 -70 Z" transform="rotate(67.5)" opacity="0.8" />
            <circle cx="0" cy="0" r="12" opacity="1" />
          </g>
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2
              id="transparency-heading"
              className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-4"
            >
              Where Your Giving Goes: <br className="hidden md:block" />
              <span className="text-[#0f766e]">Transparency & Impact</span>
            </h2>

            <p className="text-slate-500 font-medium leading-relaxed mb-8">
              SEVAA Foundation ensures that your donation makes a real
              difference. We partner with local communities so support reaches
              tribal families, rural children and vulnerable people ethically,
              responsibly and transparently.
            </p>

            <div className="grid grid-cols-2 gap-y-6 gap-x-4">
              <div className="flex items-center gap-3">
                <span className="w-4 h-4 rounded bg-[#0f766e]"></span>
                <span className="text-sm font-bold text-slate-700">
                  65% Education
                </span>
              </div>

              <div className="flex items-center gap-3">
                <span className="w-4 h-4 rounded bg-[#ffb000]"></span>
                <span className="text-sm font-bold text-slate-700">
                  20% Healthcare
                </span>
              </div>

              <div className="flex items-center gap-3">
                <span className="w-4 h-4 rounded bg-rose-500"></span>
                <span className="text-sm font-bold text-slate-700">
                  10% Food Security
                </span>
              </div>

              <div className="flex items-center gap-3">
                <span className="w-4 h-4 rounded bg-slate-300"></span>
                <span className="text-sm font-bold text-slate-700">
                  5% Operations
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <div
              className="relative w-64 h-64 rounded-full shadow-lg"
              style={{
                background:
                  "conic-gradient(#0f766e 0% 65%, #ffb000 65% 85%, #f43f5e 85% 95%, #cbd5e1 95% 100%)",
              }}
              aria-label="Donation fund allocation chart for education healthcare food security and operations"
            >
              <div className="absolute inset-0 m-auto w-44 h-44 bg-[#f8fafc] rounded-full flex items-center justify-center shadow-inner">
                <div className="text-center">
                  <p className="text-3xl font-black text-slate-900">100%</p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">
                    Accountability
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// 4. BLOG / STORIES SECTION
const StoriesSection = () => {
  const stories = [
    {
      title: "Free Health Camp for Tribal Villages",
      img: "https://images.unsplash.com/photo-1584515933487-779824d29309",
      tag: "Healthcare",
      alt: "Free health camp for tribal villages by SEVAA Foundation healthcare NGO",
    },
    {
      title: "Partnering with Local Communities",
      img: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f",
      tag: "Empowerment",
      alt: "Local community partnership and rural empowerment by SEVAA Foundation",
    },
    {
      title: "Ensuring Food Security for Vulnerable Families",
      img: "https://thumbs.dreamstime.com/b/poor-hungry-children-17518829.jpg",
      tag: "Food Security",
      alt: "Food security support for vulnerable families by SEVAA Foundation",
    },
  ];

  return (
    <section
      aria-labelledby="stories-heading"
      className="max-w-7xl mx-auto px-6 lg:px-8 mb-20 pt-10"
    >
      <div className="text-center mb-16">
        <h2
          id="stories-heading"
          className="text-3xl md:text-5xl font-black text-slate-900"
        >
          Stories That Inspire Change
        </h2>

        <p className="mt-4 text-slate-500 max-w-2xl mx-auto">
          Real stories from the ground — see how our grassroots efforts are
          transforming rural lives through education, healthcare and food
          security support.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <article className="lg:col-span-2 relative rounded-3xl overflow-hidden group cursor-pointer">
          <img
            src="https://give.do/blog/wp-content/uploads/2022/12/5-ngo-blog-banner.png"
            alt="Education support story by SEVAA Foundation for tribal children in Jharkhand"
            className="w-full h-[400px] object-cover group-hover:scale-110 transition duration-500"
            loading="lazy"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>

          <div className="absolute bottom-6 left-6 right-6 text-white">
            <p className="text-xs uppercase tracking-widest text-teal-300 mb-2">
              Education
            </p>

            <h3 className="text-2xl font-bold leading-snug">
              Bringing Schooling to the Deepest Districts of Jharkhand
            </h3>
          </div>
        </article>

        <div className="flex flex-col gap-6">
          {stories.map((blog) => (
            <article
              key={blog.title}
              className="flex gap-4 items-center bg-white rounded-2xl p-3 shadow-sm hover:shadow-md transition cursor-pointer"
            >
              <img
                src={blog.img}
                alt={blog.alt}
                className="w-24 h-24 rounded-xl object-cover"
                loading="lazy"
              />

              <div>
                <p className="text-xs text-teal-600 font-bold uppercase">
                  {blog.tag}
                </p>

                <h3 className="text-sm font-bold text-slate-900 leading-snug">
                  {blog.title}
                </h3>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// 5. MOMENTS OF IMPACT SECTION
const MomentsOfImpact = () => {
  const images = [
    {
      src: "https://thumbs.dreamstime.com/b/poor-hungry-children-9403901.jpg",
      className:
        "rounded-xl object-cover w-full h-full col-span-2 row-span-2 hover:scale-105 transition",
      alt: "Poor children food security and hunger relief support by SEVAA Foundation",
    },
    {
      src: "https://thecsrjournal.in/wp-content/uploads/2025/04/Reduced-Extreme-Poverty.jpg",
      className:
        "rounded-xl object-cover w-full h-full hover:scale-105 transition",
      alt: "Extreme poverty support and rural social welfare by SEVAA Foundation",
    },
    {
      src: "https://gyangullak.org/wp-content/uploads/2024/11/education.jpg",
      className:
        "rounded-xl object-cover w-full h-full hover:scale-105 transition",
      alt: "Tribal child education support by SEVAA Foundation NGO",
    },
    {
      src: "https://wishesandblessings.net/blog/wp-content/uploads/2022/07/WhatsApp-Image-2022-07-18-at-12.39.40-PM.jpeg",
      className:
        "rounded-xl object-cover w-full h-full col-span-2 hover:scale-105 transition",
      alt: "Charity drive and rural community support by SEVAA Foundation",
    },
    {
      src: "https://images.csmonitor.com/csm/2014/06/0623-MBOSE-PMAD-Shukla-Bose.jpg?alias=standard_1200x800",
      className:
        "rounded-xl object-cover w-full h-full hover:scale-105 transition",
      alt: "Education access for underprivileged and tribal children by SEVAA Foundation",
    },
    {
      src: "https://www.orissapost.com/wp-content/uploads/2020/07/Poor-children.jpg",
      className:
        "rounded-xl object-cover w-full h-full hover:scale-105 transition",
      alt: "Garib bachchon ki madad and rural poverty support by SEVAA Foundation",
    },
    {
      src: "https://www.rcfoundationtrust.com/assets/images/c%20edu.jpg",
      className:
        "rounded-xl object-cover w-full h-full col-span-2 hover:scale-105 transition",
      alt: "NGO education campaign for rural children by SEVAA Foundation",
    },
  ];

  return (
    <section
      aria-labelledby="moments-heading"
      className="max-w-7xl mx-auto px-6 pb-20"
    >
      <div className="text-start mb-12 py-10 border-b border-slate-300">
        <h2
          id="moments-heading"
          className="text-4xl md:text-5xl font-black text-slate-900"
        >
          Moments of Impact
        </h2>

        <p className="text-slate-500 mt-3">
          Glimpses of the lives we are changing in tribal, rural and remote
          areas.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 auto-rows-[150px]">
        {images.map((image) => (
          <img
            key={image.src}
            src={image.src}
            alt={image.alt}
            className={image.className}
            loading="lazy"
          />
        ))}
      </div>
    </section>
  );
};

// 6. ABOUT DETAILS SECTION
const AboutDetails = () => {
  return (
    <section
      id="about-details"
      aria-labelledby="about-details-heading"
      className="bg-white py-24 border-t border-slate-300"
    >
      <div className="max-w-7xl mx-auto px-6 text-slate-700 leading-relaxed text-base">
        <h2
          id="about-details-heading"
          className="text-3xl md:text-4xl font-black text-slate-900 mb-8"
        >
          About SEVAA Foundation
        </h2>

        <div className="space-y-6">
          <p>
            <strong>“सेवा परमो धर्मः”</strong> — service is our highest duty.
            SEVAA, also known as Swadesh Sevaa Charitable Foundation, is a
            people-centric nonprofit organization deeply committed to uplifting
            tribal communities and families living in extreme poverty.
          </p>

          <p>
            Based in Noida, SEVAA began its formal operations in some of the
            most economically challenged districts of Jharkhand, including
            Chatra and Pakur. These are regions that face persistent barriers
            such as limited schooling facilities, inadequate healthcare access
            and chronic food insecurity.
          </p>

          <p>
            Our programs are designed for tribal and remote rural areas, where
            daily survival often takes priority over education and health. Our
            core mission revolves around addressing three critical needs:{" "}
            <strong>education, healthcare and food security.</strong>
          </p>

          <p>
            In the field of education, we work to remove barriers for children
            in remote areas, ensuring they receive the support they need to
            learn and grow. We believe that lack of schooling facilities should
            not dictate a child&apos;s future.
          </p>

          <p>
            Healthcare access is another major challenge in tribal belts.
            Through our grassroots involvement, we assist with basic medical
            care, health awareness and support for vulnerable families during
            times of illness.
          </p>

          <p>
            Our food security initiatives are aimed at combating chronic hunger
            and ensuring that no family goes to sleep without a meal. Through
            community support and donation-driven work, we bring dignity and
            hope to families living in extreme poverty.
          </p>

          <p>
            While SEVAA is newly registered as a charitable foundation, our
            dedication is not new. Our team and volunteers have been actively
            working on the ground for years, extending informal support to those
            in need.
          </p>

          <p>
            Formal registration is a step forward that allows us to expand this
            essential work responsibly, transparently and sustainably. Looking
            ahead, our long-term vision is to extend our impact beyond
            Jharkhand, reaching other high-poverty districts in Orissa, Odisha,
            Chhattisgarh and additional underserved regions across India.
          </p>

          <p>
            We work directly at the grassroots level, partnering with local
            communities to ensure that our support reaches those who need it
            most — respectfully, ethically and with lasting impact.
          </p>

          <p>
            When you support SEVAA Foundation, you are standing with tribal
            communities, rural children, vulnerable families and people living
            in extreme poverty. Together, we can build a stronger, healthier and
            more educated tomorrow.
          </p>
        </div>

        <p className="font-bold text-lg text-[#0f766e] mt-8">
          Join us in our mission. Together, we can bring lasting change to the
          communities that need it most.
        </p>
      </div>
    </section>
  );
};

// --- MAIN EXPORTED COMPONENT ---
const About = () => {
  return (
    <>
      <Helmet>
        <title>About SEVAA Foundation | Tribal NGO in India</title>

        <meta
          name="title"
          content="About SEVAA Foundation | Tribal NGO in India"
        />

        <meta
          name="description"
          content="Learn about SEVAA Foundation, a nonprofit supporting tribal communities through education, healthcare and food security programs in rural India."
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />

        <meta
          property="og:title"
          content="About SEVAA Foundation | Tribal NGO in India"
        />

        <meta
          property="og:description"
          content="SEVAA Foundation supports tribal communities through education, healthcare and food security programs in Jharkhand and rural India."
        />

        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="About SEVAA Foundation NGO" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="About SEVAA Foundation | NGO for Tribal Communities"
        />

        <meta
          name="twitter:description"
          content="Learn about SEVAA Foundation's work for tribal communities, rural children, healthcare support and food security."
        />

        <meta name="twitter:image" content={defaultImage} />
        <meta
          name="twitter:image:alt"
          content="About SEVAA Foundation tribal community NGO in India"
        />

        <script type="application/ld+json">
          {JSON.stringify(aboutPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-white font-poppins pb-10">
        <HeroSection />
        <OurStorySection />
        <TransparencySection />

        <div className="bg-gradient-to-b from-white to-slate-50 py-10">
          <StoriesSection />
          <MomentsOfImpact />
        </div>

        <AboutDetails />

        {/* <section
          aria-labelledby="about-keywords-heading"
          className="py-14 px-6 bg-white"
        >
          <div className="max-w-7xl mx-auto">
            <h2 id="about-keywords-heading" className="sr-only">
              SEVAA Foundation About and NGO Keywords
            </h2>

            <div className="flex flex-wrap justify-center gap-2">
              {seoKeywords.map((keyword) => (
                <span
                  key={keyword}
                  className="rounded-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-[10px] md:text-xs font-bold text-slate-500 shadow-sm"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </section> */}
      </main>
    </>
  );
};

export default About;